import React, {
  type ElementType,
  type ComponentPropsWithRef,
  type JSXElementConstructor,
  type Ref,
} from 'react';
import { AtomicButton } from '@express-labs/raven-ui-atomic';
import { AtomicButtonProps } from '@express-labs/raven-ui-atomic/dist/types/AtomicButton/AtomicButton';

import {
  useHandleOnClickWithIntercept,
  useHandleOnKeyDownWithIntercept,
} from '../helpers/hook';

import { cn } from '@express-labs/raven-tools';

// polymorphic component with forwarded ref thanks to
// https://www.tsteele.dev/posts/react-polymorphic-forwardref
// https://gist.github.com/kripod/4434e7cecfdecee160026aee49ea6ee8

type IntrinsicAttributes<
  E extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>,
> = JSX.LibraryManagedAttributes<E, ComponentPropsWithRef<E>>;

export type ButtonOwnProps<E extends ElementType = ElementType> =
  AtomicButtonProps<E> & {
    /** Specify the HTML tag to render for the button.  The default tag is "button".  Only "button" and "a" are officially supported via CSS. Other tags may require more CSS. */
    as?: E;
    isAccessiblyDisabled?: boolean;
    onClick?: (ev: React.MouseEvent<HTMLElement>) => void;
    onKeyDown?: (ev: React.KeyboardEvent<HTMLElement>) => void;
  };

export type ButtonProps<E extends ElementType> = ButtonOwnProps<E> &
  Omit<IntrinsicAttributes<E>, keyof ButtonOwnProps>;

type ForwardedButtonComponent = {
  displayName: string;
  <E extends ElementType = 'button'>(props: ButtonProps<E>): JSX.Element;
};

export const Button = React.forwardRef(function Button(
  {
    as: Element = 'button',
    isAccessiblyDisabled,
    children,
    className,
    mode = 'solid',
    size = 'large',
    theme = 'dark',
    onClick,
    onKeyDown,
    ...restProps
  }: ButtonOwnProps,
  ref: Ref<Element>
) {
  const handleOnClick = useHandleOnClickWithIntercept(
    isAccessiblyDisabled,
    onClick
  );

  const handleOnKeyDown = useHandleOnKeyDownWithIntercept(
    isAccessiblyDisabled,
    onKeyDown
  );
  return (
    <AtomicButton
      aria-disabled={isAccessiblyDisabled}
      buttonState={isAccessiblyDisabled ? 'inactive' : undefined}
      as={Element}
      className={cn('rvn-ui--button', className)}
      mode={mode}
      onClick={handleOnClick}
      onKeyDown={handleOnKeyDown}
      ref={ref}
      size={size}
      theme={theme}
      {...restProps}
    >
      {children}
    </AtomicButton>
  );
}) as ForwardedButtonComponent;

Button.displayName = 'Button';

export default Button;
